// Helpers every signed-in console screen shares. The frame itself (rail, top
// bar, sign-out) is console/console-shell.tsx since D-626; this file kept its
// name so the screens' imports did not move.

import { useEffect, useRef } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Banner } from "../../../components/toast";
import { PlatformError, type PlatformMe } from "../lib/platform-api";
import { setAdminFlash } from "../lib/flash";
import { useForgetPlatformSession } from "../hooks/use-platform-session";

/** Spec 15 §3.1 step 4 (D-570 ruling 1): at three or fewer unused recovery
 * codes the console asks for a new set on every screen until there is one.
 * The count is the server's (`GET /me`), never a client tally. Drawn once,
 * by the console layout above every screen (console-routes.tsx). */
export const LOW_RECOVERY_CODES = 3;

export function LowRecoveryCodesNotice({ me }: { me: PlatformMe }) {
  const { t } = useTranslation();
  const left = me.recovery_codes_remaining;
  if (left === undefined || left === null || left > LOW_RECOVERY_CODES) return null;
  return (
    <Banner
      kind="warn"
      data-testid="admin-low-recovery-codes"
      aside={
        <Link
          to="/console/account"
          className="admin-link"
          data-testid="admin-low-recovery-codes-action"
        >
          {t("admin.staffAdmin.lowCodesAction")}
        </Link>
      }
    >
      <strong>{t("admin.staffAdmin.lowCodesTitle", { count: left })}</strong>{" "}
      {t("admin.staffAdmin.lowCodesBody")}
    </Banner>
  );
}

/**
 * A read or write on a signed-in screen that answers 401 means the session
 * died under the screen (idle timeout, the 12-hour ceiling, a sign-out in
 * another tab). Every further request would 401 the same way, so the screen
 * sends the operator to sign in with the reason, the way the account screen
 * already does.
 */
export function useSignInAgainOn401(error: unknown): void {
  const navigate = useNavigate();
  const forgetSession = useForgetPlatformSession();
  const expired = error instanceof PlatformError && error.status === 401;
  // Once per screen: the hook's helpers are new on every render, and a second
  // navigation to /login while the first is in flight helps nobody.
  const sent = useRef(false);
  useEffect(() => {
    if (!expired || sent.current) return;
    sent.current = true;
    forgetSession();
    setAdminFlash("session_expired");
    void navigate({ to: "/login" });
  }, [expired, forgetSession, navigate]);
}

/** The request id a failure carries, for the §4 error card. */
export function requestIdOf(error: unknown): string | undefined {
  return error instanceof PlatformError ? error.requestId : undefined;
}
